import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { isNil } from '@flex-design-system/fx-utils/common';
import { isBefore, startOfDay, isAfter, isSameDay } from 'date-fns';
import { useLinearLocale } from '../../locale/LocaleContext.mjs';
import { useDatePickerContext } from '../context/datePickerContext.mjs';
import { useDatePickerField } from '../hooks/useDatePickerField.mjs';
import useEffectOnce from '../hooks/useEffectOnce.mjs';
import { isDayRangeCollapsed } from '../utils/range.mjs';
import TextFieldWithErrorTooltip from './base/TextFieldWithErrorTooltip.mjs';
import { localeValues } from './localeValues.mjs';

const DATE_PICKER_INPUTS_NAME = "DatePickerInput";
const DatePickerInput = ({
  __scopeDatePicker,
  ...props
}) => {
  const {
    mode,
    selectedDate,
    onDateChange,
    startDate,
    onStartDateChange,
    endDate,
    onEndDateChange,
    excludeDates = [],
    minDate,
    maxDate
  } = useDatePickerContext(DATE_PICKER_INPUTS_NAME, __scopeDatePicker);
  const { locale } = useLinearLocale();
  const isRangeMode = mode === "range";
  const {
    notAllowedDateErrorMessage,
    invalidRangeInputErrorMessage,
    startDatePlaceholder,
    endDatePlaceholder,
    datePlaceholder
  } = localeValues[locale];
  const validateExcludeDates = (date) => {
    if (excludeDates.some((excludeDate) => isSameDay(date, excludeDate))) {
      return [false, notAllowedDateErrorMessage];
    }
    return [true, void 0];
  };
  const [
    selectedDateString,
    selectedDateFieldProps,
    selectedDateFieldState,
    onApplyCurrentSelectedDateFieldValue
  ] = useDatePickerField({
    locale,
    date: selectedDate,
    onDateChange,
    onBlur: props.onBlur,
    onChange: "onChange" in props ? props.onChange : void 0,
    onKeyDown: props.onKeyDown,
    onValidate: (date) => {
      if (!isNil(minDate) && isBefore(startOfDay(date), startOfDay(minDate)) || !isNil(maxDate) && isAfter(startOfDay(date), startOfDay(maxDate))) {
        return [false, notAllowedDateErrorMessage];
      }
      return validateExcludeDates(date);
    },
    inputType: "date"
  });
  const [
    startDateString,
    startDateFieldProps,
    startDateFieldState,
    onApplyCurrentStartDateFieldValue
  ] = useDatePickerField({
    locale,
    date: startDate,
    onDateChange: onStartDateChange,
    onBlur: props.onBlur,
    onChange: "onStartDateInputChange" in props ? props.onStartDateInputChange : void 0,
    onKeyDown: props.onKeyDown,
    onValidate: (date) => {
      if (!isNil(minDate) && isBefore(startOfDay(date), startOfDay(minDate))) {
        return [false, notAllowedDateErrorMessage];
      }
      if (endDate !== null && isDayRangeCollapsed(date, endDate)) {
        return [false, invalidRangeInputErrorMessage];
      }
      return validateExcludeDates(date);
    },
    inputType: "date"
  });
  const [
    endDateString,
    endDateFieldProps,
    endDateFieldState,
    onApplyCurrentEndDateFieldValue
  ] = useDatePickerField({
    locale,
    date: endDate,
    onDateChange: onEndDateChange,
    onBlur: props.onBlur,
    onChange: "onStartDateInputChange" in props ? props.onStartDateInputChange : void 0,
    onKeyDown: props.onKeyDown,
    onValidate: (date) => {
      if (!isNil(maxDate) && isAfter(startOfDay(date), startOfDay(maxDate))) {
        return [false, notAllowedDateErrorMessage];
      }
      if (startDate !== null && isDayRangeCollapsed(startDate, date)) {
        return [false, invalidRangeInputErrorMessage];
      }
      return validateExcludeDates(date);
    },
    inputType: "date"
  });
  const {
    autoFocus,
    disabled,
    leftSlot,
    rightSlot,
    placeholder,
    style,
    ...rest
  } = props;
  const textFieldProps = { disabled, leftSlot, rightSlot };
  useEffectOnce(() => {
    if (mode === "single") {
      onApplyCurrentSelectedDateFieldValue();
    }
    if (mode === "range") {
      onApplyCurrentStartDateFieldValue();
      onApplyCurrentEndDateFieldValue();
    }
  });
  return /* @__PURE__ */ jsx(
    TextFieldWithErrorTooltip.Wrapper,
    {
      style: {
        ...style,
        gap: isRangeMode ? 6 : 0
      },
      ...rest,
      children: isRangeMode ? /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(
          TextFieldWithErrorTooltip,
          {
            value: startDateString,
            errorTooltip: startDateFieldState.errorMessage,
            autoFocus,
            placeholder: "startPlaceholder" in props ? props.startPlaceholder : startDatePlaceholder,
            ...textFieldProps,
            ...startDateFieldProps
          }
        ),
        /* @__PURE__ */ jsx(
          TextFieldWithErrorTooltip,
          {
            value: endDateString,
            errorTooltip: endDateFieldState.errorMessage,
            placeholder: "endPlaceholder" in props ? props.endPlaceholder : endDatePlaceholder,
            ...textFieldProps,
            ...endDateFieldProps
          }
        )
      ] }) : /* @__PURE__ */ jsx(
        TextFieldWithErrorTooltip,
        {
          value: selectedDateString,
          errorTooltip: selectedDateFieldState.errorMessage,
          autoFocus,
          placeholder: props.placeholder ?? datePlaceholder,
          ...textFieldProps,
          ...selectedDateFieldProps
        }
      )
    }
  );
};

export { DatePickerInput as default };
//# sourceMappingURL=Input.mjs.map
